/**
 * Attendance Variance Task API client.
 *
 * Backend service: app/api/routers/attendance_variance.py
 * Mounted at /api/v1/hr/attendance-variance-tasks.
 *
 * 3-tier escalation: PENDING_HR → ESCALATED_GM (>24h) → ESCALATED_MD (>48h)
 * → RESOLVED / WAIVED (terminal).
 */
import apiClient from '@/shared/api/client'

export const VARIANCE_STATUSES = [
  'PENDING_HR',
  'ESCALATED_GM',
  'ESCALATED_MD',
  'RESOLVED',
  'WAIVED',
] as const
export type VarianceStatus = (typeof VARIANCE_STATUSES)[number]

export const VARIANCE_REASON_CODES = [
  'SICK',
  'MEDICAL_EMERGENCY',
  'PERSONAL',
  'MACHINE_BREAKDOWN',
  'POWER_OUTAGE',
  'LATE_ARRIVAL',
  'EARLY_DEPARTURE',
  'DEVICE_MISS',
  'UNDOCUMENTED',
  'OTHER',
] as const
export type VarianceReasonCode = (typeof VARIANCE_REASON_CODES)[number]

export interface VarianceTask {
  id: string
  employee_id: string
  emp_code: string | null
  emp_name: string | null
  department_name: string | null
  biometric_id: string | null
  on_date: string | null
  shift_id: string | null
  actual_hours: number | null
  effective_shift_hours: number | null
  variance_hours: number | null
  punches_count: number | null
  first_in_time: string | null
  last_out_time: string | null
  status: VarianceStatus
  created_at: string | null
  escalated_to_gm_at: string | null
  escalated_to_md_at: string | null
  resolved_at: string | null
  reason_code: VarianceReasonCode | null
  reason_notes: string | null
  actioned_at: string | null
  gm_acknowledged_at: string | null
  md_acknowledged_at: string | null
}

export interface VarianceSummary {
  pending_hr: number
  escalated_gm: number
  escalated_md: number
  resolved: number
  waived: number
  total_open: number
}

export interface ReasonCodeOptions {
  codes: VarianceReasonCode[]
  labels: Record<VarianceReasonCode, string>
}

export interface GenerateResult {
  on_date: string
  employees_checked: number
  created: number
  skipped_existing: number
  skipped_no_punches_absent: number
  skipped_within_tolerance: number
}

export interface EscalateResult {
  as_of: string
  promoted_to_gm: number
  promoted_to_md: number
  total_open: number
}

const BASE = '/hr/attendance-variance-tasks'

export const attendanceVarianceApi = {
  list: async (params: {
    status?: VarianceStatus
    employee_id?: string
    on_date_from?: string
    on_date_to?: string
    limit?: number
  } = {}) => {
    const r = await apiClient.get<VarianceTask[]>(BASE, { params })
    return r.data
  },

  summary: async () => {
    const r = await apiClient.get<VarianceSummary>(`${BASE}/summary`)
    return r.data
  },

  reasonCodes: async () => {
    const r = await apiClient.get<ReasonCodeOptions>(`${BASE}/reason-codes`)
    return r.data
  },

  action: async (taskId: string, body: {
    reason_code: VarianceReasonCode
    reason_notes?: string
    waive?: boolean
  }) => {
    const r = await apiClient.post<VarianceTask>(`${BASE}/${taskId}/action`, body)
    return r.data
  },

  gmAcknowledge: async (taskId: string) => {
    const r = await apiClient.post<VarianceTask>(`${BASE}/${taskId}/gm-ack`)
    return r.data
  },

  mdAcknowledge: async (taskId: string) => {
    const r = await apiClient.post<VarianceTask>(`${BASE}/${taskId}/md-ack`)
    return r.data
  },

  generateForDate: async (on_date: string) => {
    const r = await apiClient.post<GenerateResult>(`${BASE}/generate-for-date`, { on_date })
    return r.data
  },

  escalatePending: async () => {
    const r = await apiClient.post<EscalateResult>(`${BASE}/escalate-pending`, {})
    return r.data
  },
}

// ─── UI helpers ─────────────────────────────────────────────────────────

export const STATUS_LABEL: Record<VarianceStatus, string> = {
  PENDING_HR: 'Pending HR',
  ESCALATED_GM: 'Escalated to GM',
  ESCALATED_MD: 'Escalated to MD',
  RESOLVED: 'Resolved',
  WAIVED: 'Waived',
}

export const STATUS_COLOR: Record<VarianceStatus, string> = {
  PENDING_HR: 'gold',
  ESCALATED_GM: 'orange',
  ESCALATED_MD: 'red',
  RESOLVED: 'green',
  WAIVED: 'default',
}

export const isOpenStatus = (s: VarianceStatus) =>
  s === 'PENDING_HR' || s === 'ESCALATED_GM' || s === 'ESCALATED_MD'
